import React, { useEffect, useState } from "react";
import { Country, State, City } from "country-state-city";

const CountryStateCitySelector = ({ onChange, countryValue, stateValue, cityValue }) => {
  const [country, setCountry] = useState(countryValue || "");
  const [state, setState] = useState(stateValue || "");
  const [city, setCity] = useState(cityValue || "");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // ✅ When `countryValue` changes, update the country and fetch its states
  useEffect(() => {
    if (countryValue) {
      setCountry(countryValue);
      const countryData = Country.getAllCountries().find(c => c.name === countryValue);
      const countryISO = countryData ? countryData.isoCode : "";

      if (countryISO) {
        const newStates = State.getStatesOfCountry(countryISO) || [];
        setStates(newStates);
      }
    }
  }, [countryValue]);

  // ✅ When `stateValue` changes, update the state and fetch its cities
  useEffect(() => {
    if (stateValue && country) {
      setState(stateValue);
      const countryData = Country.getAllCountries().find(c => c.name === country);
      const countryISO = countryData ? countryData.isoCode : "";
      const stateData = State.getStatesOfCountry(countryISO).find(s => s.name === stateValue);

      if (stateData) {
        setCities(City.getCitiesOfState(countryISO, stateData.isoCode) || []);
      }
    }
  }, [stateValue, country]);

  // ✅ When `cityValue` changes, update the city
  useEffect(() => {
    if (cityValue) {
      setCity(cityValue);
    }
  }, [cityValue]);

  // ✅ Handle country selection
  const handleCountryChange = (event) => {
    const selectedCountryName = event.target.value;
    setCountry(selectedCountryName);
    setState("");
    setCity("");

    const countryData = Country.getAllCountries().find(c => c.name === selectedCountryName);
    const countryISO = countryData ? countryData.isoCode : "";
    setStates(State.getStatesOfCountry(countryISO) || []);
    setCities([]);

    onChange({ country: selectedCountryName, state: "", city: "" });
  };

  // ✅ Handle state selection
  const handleStateChange = (event) => {
    const selectedStateName = event.target.value;
    setState(selectedStateName);
    setCity("");

    const countryData = Country.getAllCountries().find(c => c.name === country);
    const countryISO = countryData ? countryData.isoCode : "";
    const stateData = State.getStatesOfCountry(countryISO).find(s => s.name === selectedStateName);

    if (stateData) {
      setCities(City.getCitiesOfState(countryISO, stateData.isoCode) || []);
    }

    onChange({ country, state: selectedStateName, city: "" });
  };

  // ✅ Handle city selection
  const handleCityChange = (event) => {
    const selectedCityName = event.target.value;
    setCity(selectedCityName);
    onChange({ country, state, city: selectedCityName });
  };

  return (
    <div>
      {/* Country Dropdown */}
      <div className="mb-3">
        <label htmlFor="country" className="form-label">Country  <span className="text-danger">*</span></label>
        <select id="country" className="form-select" value={country} onChange={handleCountryChange}>
          <option value="">--Select Country--</option>
          {Country.getAllCountries().map((c) => (
            <option key={c.isoCode} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* State Dropdown */}
      <div className="mb-3">
        <label htmlFor="state" className="form-label">State <span className="text-danger">*</span></label>
        <select
          id="state"
          className="form-select"
          value={state}
          onChange={handleStateChange}
          disabled={!states.length}
        >
          <option value="">--Select State--</option>
          {states.map((s) => (
            <option key={s.isoCode} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* City Dropdown */}
      <div className="mb-3">
        <label htmlFor="city" className="form-label">City <span className="text-danger">*</span></label>
        <select
          id="city"
          className="form-select"
          value={city}
          onChange={handleCityChange}
          disabled={!cities.length}
        >
          <option value="">--Select City--</option>
          {cities.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default CountryStateCitySelector;
